import { Car, MapPin, Users, Wifi, Snowflake, Zap } from 'lucide-react';
import { useTranslation } from '../../../i18n/i18n';

const TransferView = ({ product, lang }) => {
  const { t } = useTranslation(lang);

  // Données mock pour le transfert
  const transferData = {
    vehiclesCount: "2 véhicule(s)",
    transferInfo: {
      type: "Taxi",
      duration: "N/A minutes"
    },
    itinerary: {
      departure: {
        location: "N/A",
        type: "Aéroport"
      },
      arrival: {
        location: "N/A", 
        type: "Aéroport"
      }
    },
    vehicles: [
      {
        type: "Compacte",
        model: "Corolla",
        capacity: 3,
        tariffsCount: 0,
        amenities: ["Port USB", "wifi"],
        noTariffMessage: "Aucun tarif configuré pour ce véhicule"
      },
      {
        type: "Berline", 
        model: "Rav4",
        capacity: 2,
        tariffsCount: 0,
        amenities: ["Climatisation"],
        noTariffMessage: "Aucun tarif configuré pour ce véhicule"
      }
    ]
  };

  const getAmenityIcon = (amenity) => {
    switch(amenity.toLowerCase()) {
      case 'wifi': return <Wifi size={16} />;
      case 'climatisation': return <Snowflake size={16} />;
      case 'port usb': return <Zap size={16} />;
      default: return <div style={{width: '16px', height: '16px'}} />;
    }
  };

  const styles = {
    section: {
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '24px'
    },
    infoCard: {
      background: '#f8fafc',
      padding: '20px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb'
    },
    infoItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 0',
      borderBottom: '1px solid #f3f4f6'
    },
    infoLabel: {
      fontWeight: '600',
      color: '#374151',
      fontSize: '14px'
    },
    infoValue: {
      color: '#6b7280',
      fontSize: '14px'
    },
    itineraryCard: {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      padding: '24px',
      borderRadius: '12px',
      border: '1px solid #bae6fd'
    },
    itineraryStep: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginBottom: '20px'
    },
    stepIcon: {
      width: '40px',
      height: '40px',
      background: '#7c3aed',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      flexShrink: 0
    },
    stepContent: {
      flex: 1
    },
    stepLocation: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '4px'
    },
    stepType: {
      fontSize: '14px',
      color: '#6b7280'
    },
    vehicleCard: {
      background: '#f8fafc',
      padding: '20px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      marginBottom: '16px'
    },
    vehicleHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'start',
      marginBottom: '16px'
    },
    vehicleName: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937'
    },
    vehicleModel: {
      fontSize: '14px',
      color: '#6b7280',
      marginTop: '4px'
    },
    capacityBadge: {
      padding: '4px 8px',
      background: '#10b981',
      color: 'white',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    tariffsBadge: {
      padding: '4px 8px',
      background: '#f59e0b',
      color: 'white',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      marginTop: '8px'
    },
    amenitiesList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px',
      marginTop: '12px'
    },
    amenityItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      background: '#f0f9ff',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '500'
    },
    noTariffMessage: {
      textAlign: 'center',
      padding: '16px',
      background: '#fef3c7',
      borderRadius: '6px',
      color: '#92400e',
      fontSize: '14px',
      fontWeight: '500',
      marginTop: '12px'
    }
  };

  return (
    <>
      {/* Détails du Transfert */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <Car size={24} />
          {t('transferDetails')}
        </h2>
        <div style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>
          {transferData.vehiclesCount}
        </div>

        {/* Informations du Transfert */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('transferInformation')}</h3>
          <div style={styles.grid}>
            <div style={styles.infoCard}>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>{t('transferType')}</span>
                <span style={styles.infoValue}>{transferData.transferInfo.type}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>{t('duration')}</span>
                <span style={styles.infoValue}>{transferData.transferInfo.duration}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Itinéraire */}
        <div>
          <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('itinerary')}</h3>
          <div style={styles.itineraryCard}>
            {/* Départ */}
            <div style={styles.itineraryStep}>
              <div style={styles.stepIcon}>
                <MapPin size={20} />
              </div>
              <div style={styles.stepContent}>
                <div style={styles.stepLocation}>{transferData.itinerary.departure.location}</div>
                <div style={styles.stepType}>{transferData.itinerary.departure.type}</div>
              </div>
              <div style={{ fontSize: '14px', color: '#7c3aed', fontWeight: '600' }}>
                {t('departure')}
              </div>
            </div>

            {/* Ligne de séparation */}
            <div style={{
              height: '2px',
              background: 'linear-gradient(90deg, #7c3aed 0%, transparent 100%)',
              margin: '20px 0 20px 40px',
              opacity: 0.3
            }} />

            {/* Arrivée */}
            <div style={styles.itineraryStep}>
              <div style={styles.stepIcon}>
                <MapPin size={20} />
              </div>
              <div style={styles.stepContent}>
                <div style={styles.stepLocation}>{transferData.itinerary.arrival.location}</div>
                <div style={styles.stepType}>{transferData.itinerary.arrival.type}</div>
              </div>
              <div style={{ fontSize: '14px', color: '#10b981', fontWeight: '600' }}>
                {t('arrival')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Véhicules Disponibles */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <Users size={24} />
          {t('availableVehicles')} ({transferData.vehicles.length} {t('vehicle')}(s))
        </h2>
        
        {transferData.vehicles.map((vehicle, index) => (
          <div key={index} style={styles.vehicleCard}>
            <div style={styles.vehicleHeader}>
              <div>
                <div style={styles.vehicleName}>{vehicle.type}</div>
                <div style={styles.vehicleModel}>{vehicle.model} • {t('capacity')}: {vehicle.capacity} {t('people')}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap : '3px' }}>
                <div style={styles.capacityBadge}>
                  <Users size={12} />
                  {vehicle.capacity}
                </div>
                <div style={styles.tariffsBadge}>
                  {vehicle.tariffsCount} {t('tariff')}(s)
                </div>
              </div>
            </div>

            {/* Équipements */}
            {vehicle.amenities.length > 0 && (
              <div>
                <strong style={{ fontSize: '14px', color: '#374151' }}>{t('amenities')}:</strong>
                <div style={styles.amenitiesList}>
                  {vehicle.amenities.map((amenity, amenityIndex) => (
                    <div key={amenityIndex} style={styles.amenityItem}>
                      {getAmenityIcon(amenity)}
                      {amenity}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Message aucun tarif */}
            <div style={styles.noTariffMessage}>
              {vehicle.noTariffMessage}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default TransferView;